import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download, ExternalLink } from 'lucide-react';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { Button } from '@/components/ui/button';
import { useSession } from '@/lib/session';
import { cn } from '@/lib/utils';
import { useAddNote, useExhibitors, useMnaCompany, usePapers, useRetireCompany, useSetStage } from '../lib/api';
import type {
  AssetDto,
  CompanyDossierDto,
  CompanyFact,
  CompanyScore,
  FactKey,
  OwnershipSegment,
  ScoreHistoryPoint,
  WatchlistStage,
} from '../types';
import {
  ConfidenceValue,
  DistressChip,
  EventKindBadge,
  Flag,
  OwnershipBadge,
  SIZE_BAND_LABELS,
  SourceLink,
  WATCHLIST_LABELS,
  WatchlistBadge,
  FIT_HELP,
  fmtDate,
  scoreTone,
} from './_shared';
import { AssetRow } from './Assets';

// Tab 3b — Company dossier, a full route view at /mna/companies/:slug (WWD's spec modal,
// graduated to a page: dossiers are deep, deep-linkable objects). Every fact renders
// through ConfidenceValue; scores carry their cited dimensions; events are the
// append-only timeline.

const FINANCIAL_KEYS: FactKey[] = [
  'revenue',
  'ebitda',
  'ebitda_margin_pct',
  'net_debt',
  'total_assets',
  'equity',
  'net_income',
  'ebit',
  'current_assets',
  'current_liabilities',
  'retained_earnings',
  'employees',
];

const CORPORATE_KEYS: FactKey[] = [
  'founded_year',
  'ownership_notes',
  'parent_company',
  'pe_fund_vintage',
  'leadership_ceo',
  'headcount_trend',
  'credit_rating',
  'listed_exchange',
  'auditor',
];

const FACT_LABEL_OVERRIDES: Partial<Record<FactKey, string>> = {
  ebitda: 'EBITDA',
  ebitda_margin_pct: 'EBITDA margin',
  ebit: 'EBIT',
  oem_service_mix: 'OEM / service mix',
  ip_patents: 'IP & patents',
  leadership_ceo: 'CEO',
  pe_fund_vintage: 'PE fund vintage',
};

function factLabel(key: FactKey): string {
  const override = FACT_LABEL_OVERRIDES[key];
  if (override) return override;
  return key
    .split('_')
    .map((p, i) => (i === 0 ? p.charAt(0).toUpperCase() + p.slice(1) : p))
    .join(' ');
}

export default function CompanyDossier() {
  const { slug } = useParams<{ slug: string }>();
  const { data: dossier, isLoading, error } = useMnaCompany(slug);

  if (isLoading) return <LoadingSkeleton lines={10} />;

  const status = (error as { status?: number } | null)?.status;
  if (status === 404 || (!isLoading && !error && !dossier)) {
    return (
      <div className="stagger">
        <BackLink />
        <Card>
          <CardTitle>Company not found</CardTitle>
          <CardDescription className="mt-2">No company with slug “{slug}” is in the tracked universe.</CardDescription>
        </Card>
      </div>
    );
  }
  if (error || !dossier) return <ErrorState message={(error as Error | null)?.message} />;

  const financials = dossier.facts.filter((f) => FINANCIAL_KEYS.includes(f.key));
  const corporate = dossier.facts.filter((f) => CORPORATE_KEYS.includes(f.key));
  const capabilities = dossier.facts.filter((f) => !FINANCIAL_KEYS.includes(f.key) && !CORPORATE_KEYS.includes(f.key));

  return (
    <div className="stagger">
      <BackLink />

      <Header dossier={dossier} />

      {dossier.status === 'retired' && <RetiredBanner dossier={dossier} />}

      {/* key = slug: the route component is reused across /companies/:slug navigations —
          remount so the stage selector re-seeds from the new dossier. */}
      <WorkflowCard key={dossier.slug} dossier={dossier} />

      {dossier.score ? (
        <ScoreBreakdown score={dossier.score} />
      ) : (
        <Card>
          <span className="eyebrow">Scores</span>
          <p className="mt-2 text-sm italic text-muted opacity-70">Not yet scored — universe member awaiting the next fit-score run.</p>
        </Card>
      )}

      {dossier.scoreHistory.length >= 2 && <ScoreHistory history={dossier.scoreHistory} />}

      <Card>
        <span className="eyebrow">Facts</span>
        <FactSection title="Financials" facts={sortFinancials(financials)} showFy />
        <FactSection title="Capabilities" facts={capabilities} />
        <FactSection title="Corporate" facts={corporate} />
      </Card>

      <ResearchCard slug={dossier.slug} />

      <ExhibitorCard slug={dossier.slug} />

      <EventsTimeline dossier={dossier} />

      {dossier.ownershipHistory.length > 0 && <OwnershipHistory segments={dossier.ownershipHistory} />}

      {dossier.assets.length > 0 && (
        <Card>
          <span className="eyebrow">Assets</span>
          <div className="mt-3 flex flex-col gap-3">
            {dossier.assets.map((a: AssetDto) => (
              <AssetRow key={a.id} asset={a} linkCompany={false} />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

/** Track A: the audit-trail view of a retired company — data intact, surfaces excluded. */
function RetiredBanner({ dossier: d }: { dossier: CompanyDossierDto }) {
  const { data: session } = useSession();
  const isAdmin = !!session?.isAdmin;
  const retire = useRetireCompany(d.slug);

  return (
    <Card className="border-accent-danger">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="font-(family-name:--font-ui) text-xs font-semibold uppercase tracking-[0.12em] text-accent-danger">
            Retired
          </span>
          <p className="mt-1 text-sm text-muted">
            This company was retired from the tracked universe: it is excluded from scoring, the radar,
            the distress board, briefs and alerts. Its events and history are retained (append-only), and
            new signals land in the review queue instead of publishing.
          </p>
        </div>
        {isAdmin && (
          <Button
            size="sm"
            disabled={retire.isPending}
            onClick={() => retire.mutate({ action: 'unretire' })}
          >
            Un-retire
          </Button>
        )}
      </div>
      {retire.error && <p className="mt-2 text-sm text-accent-danger">{(retire.error as Error).message}</p>}
    </Card>
  );
}

function BackLink() {
  return (
    <Link
      to="/mna/companies"
      className="mb-4 inline-flex items-center gap-1 text-sm text-muted hover:text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden /> All companies
    </Link>
  );
}

/* ---------- header ---------- */

function Header({ dossier: d }: { dossier: CompanyDossierDto }) {
  const { data: session } = useSession();
  const isAdmin = !!session?.isAdmin;
  const retire = useRetireCompany(d.slug);
  // Two-click confirm (the review-queue guardrail idiom) — retiring drops the company
  // from every surface, so a stray click must not do it.
  const [confirmingRetire, setConfirmingRetire] = useState(false);

  const legalIds: Array<{ label: string; value: string }> = [
    d.legalIds.lei && { label: 'LEI', value: d.legalIds.lei },
    d.legalIds.noOrgnr && { label: 'Org.nr (NO)', value: d.legalIds.noOrgnr },
    d.legalIds.ukCrn && { label: 'CRN (UK)', value: d.legalIds.ukCrn },
    d.legalIds.usCik && { label: 'CIK (US)', value: d.legalIds.usCik },
    d.legalIds.deHrb && { label: 'HRB (DE)', value: d.legalIds.deHrb },
  ].filter((x): x is { label: string; value: string } => Boolean(x));

  return (
    <Card>
      {/* Note: sub-segment badges live on cards/radar — CompanyDossierDto carries them
          as the 'sub_segments' fact, not a header field (types.ts contract). */}
      <div className="flex flex-wrap items-center gap-1.5">
        {/* Same-origin <a> — rides the portal BFF proxy with the session cookie. */}
        <Button asChild size="sm">
          <a href={`/api/mna/companies/${encodeURIComponent(d.slug)}/export`} download>
            <Download className="h-3.5 w-3.5" aria-hidden /> Export dossier (.docx)
          </a>
        </Button>
        {isAdmin && d.status !== 'retired' && (
          confirmingRetire ? (
            <span className="flex items-center gap-1.5">
              <Button
                size="sm"
                variant="danger"
                disabled={retire.isPending}
                onClick={() => retire.mutate({ action: 'retire' }, { onSettled: () => setConfirmingRetire(false) })}
              >
                Confirm retire {d.name}?
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmingRetire(false)}>
                Cancel
              </Button>
            </span>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setConfirmingRetire(true)}>
              Retire…
            </Button>
          )
        )}
        <span className="ml-auto flex flex-wrap items-center gap-1.5">
          {d.status !== 'active' && (
            <span className="rounded-(--radius-sm) border border-accent-danger bg-surface-elevated px-2 py-0.5 font-(family-name:--font-ui) text-[0.65rem] font-medium uppercase tracking-[0.12em] text-accent-danger">
              {d.status.replace(/_/g, ' ')}
            </span>
          )}
          <DistressChip stage={d.distressStage} />
          <WatchlistBadge stage={d.watchlistStage} />
        </span>
      </div>

      <h2 className="mt-3 flex items-center gap-2 font-(family-name:--font-heading) text-2xl font-bold text-text">
        <Flag countryCode={d.hqCountry} />
        {d.name}
        {d.ticker && (
          <span className="font-(family-name:--font-ui) text-sm font-normal text-muted [font-variant-numeric:tabular-nums]">
            {d.ticker}
          </span>
        )}
      </h2>

      {(d.nameAliases.length > 0 || d.formerNames.length > 0) && (
        <p className="mt-0.5 text-xs italic text-muted">
          {d.nameAliases.length > 0 && <>aka {d.nameAliases.join(', ')}</>}
          {d.nameAliases.length > 0 && d.formerNames.length > 0 && ' · '}
          {d.formerNames.length > 0 && <>ex {d.formerNames.join(', ')}</>}
        </p>
      )}

      {d.description && <p className="mt-3 max-w-3xl text-sm text-muted">{d.description}</p>}

      <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-4">
        <HeaderField label="Ownership">
          <span className="flex flex-wrap items-center gap-1.5">
            <OwnershipBadge ownershipType={d.ownershipType} peSponsor={d.peSponsor} />
          </span>
          {d.peSponsor && (
            <span className="mt-1 block text-xs text-muted">
              {d.peSponsor}
              {d.peEntryYear && <> — since {d.peEntryYear}</>}
            </span>
          )}
        </HeaderField>
        <HeaderField label="Size band">{SIZE_BAND_LABELS[d.sizeBand]}</HeaderField>
        <HeaderField label="Operating countries">
          <span className="flex flex-wrap items-center gap-1.5">
            {d.operatingCountries.map((cc) => (
              <Flag key={cc} countryCode={cc} />
            ))}
            {d.operatingCountries.length === 0 && '—'}
          </span>
        </HeaderField>
        <HeaderField label="Website">
          {d.website ? (
            <a
              href={d.website}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
              {hostOf(d.website)}
            </a>
          ) : (
            '—'
          )}
        </HeaderField>
        {legalIds.map((id) => (
          <HeaderField key={id.label} label={id.label}>
            <span className="font-mono text-xs">{id.value}</span>
          </HeaderField>
        ))}
      </dl>
    </Card>
  );
}

function HeaderField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="font-(family-name:--font-ui) text-[0.65rem] uppercase tracking-[0.12em] text-muted">{label}</dt>
      <dd className="mt-0.5 text-text">{children}</dd>
    </div>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/* ---------- workflow (Phase D — stage + history + notes) ---------- */

const fieldClass =
  'bg-surface-elevated border border-border rounded-(--radius-sm) px-3 py-2 text-sm ' +
  'font-(family-name:--font-body) text-text focus:outline-2 focus:outline-offset-2 focus:outline-accent';

const WATCHLIST_STAGES = Object.keys(WATCHLIST_LABELS) as WatchlistStage[];

/** Stage workflow + notes. Everyone reads; only portal admins get the write inputs
 *  (the same session gate MnaApp uses for the Debug tab — MNA.Api re-checks server-side). */
function WorkflowCard({ dossier: d }: { dossier: CompanyDossierDto }) {
  const { data: session } = useSession();
  const isAdmin = !!session?.isAdmin;

  const setStage = useSetStage(d.slug);
  const addNote = useAddNote(d.slug);

  const [stage, setStageValue] = useState<WatchlistStage>(d.watchlistStage ?? 'radar');
  const [stageNote, setStageNote] = useState('');
  const [noteText, setNoteText] = useState('');

  const history = [...d.watchlistHistory].sort((a, b) => b.at.localeCompare(a.at));
  const notes = [...d.notes].sort((a, b) => b.at.localeCompare(a.at));

  return (
    <Card>
      <span className="eyebrow">Workflow</span>

      {/* current stage + (admin) transition */}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <span className="text-sm text-muted">Current stage</span>
        {d.watchlistStage ? (
          <WatchlistBadge stage={d.watchlistStage} />
        ) : (
          <span className="text-sm italic text-muted opacity-70">not on the watchlist</span>
        )}
        {isAdmin && (
          <span className="flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="mna-stage-select">
              New stage
            </label>
            <select
              id="mna-stage-select"
              className={cn(fieldClass, 'min-h-9')}
              value={stage}
              onChange={(e) => setStageValue(e.target.value as WatchlistStage)}
            >
              {WATCHLIST_STAGES.map((s) => (
                <option key={s} value={s}>
                  {WATCHLIST_LABELS[s]}
                </option>
              ))}
            </select>
            <input
              type="text"
              className={cn(fieldClass, 'min-h-9 w-72 max-w-full')}
              placeholder="Note (optional) — why the change"
              value={stageNote}
              onChange={(e) => setStageNote(e.target.value)}
            />
            <Button
              size="sm"
              variant="primary"
              disabled={setStage.isPending || stage === d.watchlistStage}
              onClick={() =>
                setStage.mutate(
                  { stage, note: stageNote.trim() || null },
                  { onSuccess: () => setStageNote('') }
                )
              }
            >
              Set stage
            </Button>
          </span>
        )}
      </div>
      {setStage.error && <p className="mt-2 text-sm text-accent-danger">{(setStage.error as Error).message}</p>}

      {/* stage history timeline */}
      <section className="mt-5">
        <h3 className="font-(family-name:--font-ui) text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-muted">
          Stage history
        </h3>
        {history.length === 0 ? (
          <p className="mt-2 text-sm italic text-muted opacity-70">No stage changes on record.</p>
        ) : (
          <ol className="mt-2 flex flex-col">
            {history.map((h, i) => (
              <li key={`${h.at}-${i}`} className="flex gap-4 border-b border-border py-2.5 last:border-0">
                <span className="w-24 flex-shrink-0 pt-0.5 text-xs text-muted [font-variant-numeric:tabular-nums]">
                  {fmtDate(h.at)}
                </span>
                <div className="min-w-0 flex-1">
                  <WatchlistBadge stage={h.stage} />
                  {h.note && <p className="mt-1 text-sm text-muted">{h.note}</p>}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* notes */}
      <section className="mt-5">
        <h3 className="font-(family-name:--font-ui) text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-muted">
          Notes
        </h3>
        {notes.length === 0 ? (
          <p className="mt-2 text-sm italic text-muted opacity-70">No notes yet.</p>
        ) : (
          <ol className="mt-2 flex flex-col">
            {notes.map((n) => (
              <li key={n.id} className="flex gap-4 border-b border-border py-2.5 last:border-0">
                <span className="w-24 flex-shrink-0 pt-0.5 text-xs text-muted [font-variant-numeric:tabular-nums]">
                  {fmtDate(n.at)}
                </span>
                <p className="min-w-0 flex-1 text-sm text-text">{n.text}</p>
              </li>
            ))}
          </ol>
        )}
        {isAdmin && (
          <div className="mt-3 flex flex-col gap-2">
            <label className="sr-only" htmlFor="mna-add-note">
              Add note
            </label>
            <textarea
              id="mna-add-note"
              rows={2}
              className={cn(fieldClass, 'max-w-2xl resize-y')}
              placeholder="Add a note…"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
            <div>
              <Button
                size="sm"
                disabled={addNote.isPending || noteText.trim() === ''}
                onClick={() =>
                  addNote.mutate({ text: noteText.trim() }, { onSuccess: () => setNoteText('') })
                }
              >
                Add note
              </Button>
            </div>
            {addNote.error && <p className="text-sm text-accent-danger">{(addNote.error as Error).message}</p>}
          </div>
        )}
      </section>
    </Card>
  );
}

/* ---------- score breakdown ---------- */

function ScoreBreakdown({ score }: { score: CompanyScore }) {
  return (
    <Card>
      <span className="eyebrow">Score breakdown</span>
      <div className="mt-3 flex flex-wrap gap-x-10 gap-y-3">
        <BigScore label="Fit" value={score.fitScore} help={FIT_HELP} />
        <BigScore label="Availability" value={score.availabilityScore} />
        <BigScore label="Composite" value={score.composite} />
        <div className="ml-auto self-end text-right text-xs text-muted">
          rubric {score.rubricVersion} · scored {fmtDate(score.scoredAt)}
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-4">
        {score.fitDimensions.map((dim) => (
          <div key={dim.dimension}>
            <div className="flex items-center gap-3">
              <span className="w-56 flex-shrink-0 truncate font-(family-name:--font-ui) text-xs font-medium uppercase tracking-[0.12em] text-text">
                {dim.dimension.replace(/_/g, ' ')}
              </span>
              <span className="h-2.5 flex-1 overflow-hidden rounded-[2px] bg-surface-elevated">
                <span className="block h-full rounded-[2px] bg-accent" style={{ width: `${dim.score}%` }} />
              </span>
              <span
                className={cn(
                  'w-8 flex-shrink-0 text-right font-(family-name:--font-ui) font-semibold [font-variant-numeric:tabular-nums]',
                  scoreTone(dim.score)
                )}
              >
                {dim.score}
              </span>
            </div>
            <p className="mt-1.5 text-sm text-muted">{dim.rationale}</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {dim.citations.map((c, i) => (
                <span
                  key={i}
                  title={`${c.kind} #${c.refId}`}
                  className="inline-block rounded-(--radius-sm) border border-border bg-surface-elevated px-2 py-0.5 text-[0.7rem] text-muted font-(family-name:--font-ui)"
                >
                  {c.kind === 'event' ? '⚡ ' : '¶ '}
                  {c.label}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {(score.availabilityComponents?.length ?? 0) > 0 && (
        <div className="mt-6">
          <div
            title="Deterministic availability-v1: per-kind decayed event points + computed signals (PE-hold tiers, leverage, Z''-score, Zmijewski, revenue/margin trajectory). Every component cites its underlying facts or events."
            className="cursor-help font-(family-name:--font-ui) text-[0.7rem] uppercase tracking-[0.14em] text-muted"
          >
            Availability breakdown (deterministic)
          </div>
          <div className="mt-2 flex flex-col gap-1.5">
            {score.availabilityComponents!.map((c) => (
              <div key={c.key} className="flex items-baseline gap-3">
                <span className="w-56 flex-shrink-0 truncate font-(family-name:--font-ui) text-xs font-medium uppercase tracking-[0.12em] text-text">
                  {c.key.startsWith('event:') ? `⚡ ${c.key.slice(6).replace(/_/g, ' ')}` : c.key.replace(/_/g, ' ')}
                </span>
                <span
                  className={cn(
                    'w-12 flex-shrink-0 text-right font-(family-name:--font-ui) text-sm font-semibold [font-variant-numeric:tabular-nums]',
                    c.points < 0 ? 'text-accent-success' : undefined
                  )}
                >
                  {c.points > 0 ? `+${c.points}` : c.points}
                </span>
                <span className="min-w-0 flex-1 truncate text-xs text-muted" title={c.detail}>
                  {c.detail}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function BigScore({ label, value, help }: { label: string; value: number | null; help?: string }) {
  return (
    <div title={help} className={help ? 'cursor-help' : undefined}>
      <div className="font-(family-name:--font-ui) text-[0.7rem] uppercase tracking-[0.14em] text-muted">{label}</div>
      {value === null ? (
        <div className="mt-1 text-2xl text-muted">—</div>
      ) : (
        <div className={cn('mt-1 font-(family-name:--font-heading) text-3xl font-bold [font-variant-numeric:tabular-nums]', scoreTone(value))}>
          {value}
        </div>
      )}
    </div>
  );
}

/* ---------- score history sparkline (plain SVG) ---------- */

const SPARK_W = 640;
const SPARK_H = 120;
const SPARK_PAD = 10;

function sparkPath(history: ScoreHistoryPoint[], pick: (p: ScoreHistoryPoint) => number | null): string {
  const usable = history.filter((p) => pick(p) !== null);
  if (usable.length < 2) return '';
  const step = (SPARK_W - 2 * SPARK_PAD) / (history.length - 1);
  return history
    .map((p, i) => {
      const v = pick(p);
      if (v === null) return null;
      const x = SPARK_PAD + i * step;
      const y = SPARK_PAD + (1 - v / 100) * (SPARK_H - 2 * SPARK_PAD);
      return `${x},${y}`;
    })
    .filter((s): s is string => s !== null)
    .map((s, i) => (i === 0 ? `M${s}` : `L${s}`))
    .join(' ');
}

function ScoreHistory({ history }: { history: ScoreHistoryPoint[] }) {
  const sorted = [...history].sort((a, b) => a.scoredAt.localeCompare(b.scoredAt));
  return (
    <Card>
      <span className="eyebrow">Score history</span>
      <div className="mt-3">
        <svg viewBox={`0 0 ${SPARK_W} ${SPARK_H}`} className="w-full max-w-2xl" role="img" aria-label="Fit and availability score history">
          {[0, 50, 100].map((t) => (
            <line
              key={t}
              x1={SPARK_PAD}
              x2={SPARK_W - SPARK_PAD}
              y1={SPARK_PAD + (1 - t / 100) * (SPARK_H - 2 * SPARK_PAD)}
              y2={SPARK_PAD + (1 - t / 100) * (SPARK_H - 2 * SPARK_PAD)}
              stroke="var(--border)"
              strokeOpacity={0.6}
            />
          ))}
          <path d={sparkPath(sorted, (p) => p.fitScore)} fill="none" stroke="var(--accent)" strokeWidth={2} />
          <path d={sparkPath(sorted, (p) => p.availabilityScore)} fill="none" stroke="var(--accent-info)" strokeWidth={2} strokeDasharray="4 3" />
        </svg>
        <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-muted">
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden className="inline-block h-0.5 w-5 bg-accent" /> Fit
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden className="inline-block h-0.5 w-5 border-t-2 border-dashed border-accent-info" /> Availability
          </span>
          <span className="[font-variant-numeric:tabular-nums]">
            {fmtDate(sorted[0].scoredAt)} → {fmtDate(sorted[sorted.length - 1].scoredAt)}
          </span>
        </div>
      </div>
    </Card>
  );
}

/* ---------- facts ---------- */

/** Financial rows sort by catalog key order, then fiscal year descending. */
function sortFinancials(facts: CompanyFact[]): CompanyFact[] {
  return [...facts].sort(
    (a, b) => FINANCIAL_KEYS.indexOf(a.key) - FINANCIAL_KEYS.indexOf(b.key) || (b.fy ?? 0) - (a.fy ?? 0)
  );
}

function FactSection({ title, facts, showFy }: { title: string; facts: CompanyFact[]; showFy?: boolean }) {
  // Disclosed facts render; not_disclosed rows collapse behind a one-line toggle. The full
  // 39-key catalog rendered as rows made sparse dossiers (Edyce: ~30 not-disclosed keys)
  // scroll for screens of italic placeholders.
  const [showAll, setShowAll] = useState(false);
  if (facts.length === 0) return null;
  const disclosed = facts.filter((f) => f.confidence !== 'not_disclosed');
  const hidden = facts.length - disclosed.length;
  const visible = showAll ? facts : disclosed;
  return (
    <section className="mt-4">
      <h3 className="font-(family-name:--font-ui) text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-muted">{title}</h3>
      {visible.length === 0 && (
        <p className="mt-2 text-sm italic text-muted opacity-70">Nothing disclosed yet.</p>
      )}
      <table className="mt-2 w-full text-sm">
        <tbody>
          {visible.map((f, i) => (
            <tr key={`${f.key}-${f.fy ?? ''}-${i}`} className="border-b border-border align-top last:border-0">
              <th
                scope="row"
                className="w-44 py-2 pr-3 text-left font-(family-name:--font-ui) text-[0.7rem] font-medium uppercase tracking-[0.12em] text-muted"
              >
                {factLabel(f.key)}
              </th>
              {showFy && (
                <td className="w-16 py-2 pr-3 text-muted [font-variant-numeric:tabular-nums]">{f.fy ?? ''}</td>
              )}
              <td className="py-2 pr-3">
                {/* reported facts carry their source inline (the WWD dayrate discipline) */}
                <ConfidenceValue
                  value={f.value}
                  confidence={f.confidence}
                  estBasis={f.estBasis}
                  source={f.confidence === 'reported' ? f.source : null}
                />
              </td>
              <td className="w-8 py-2 text-right">
                {f.source && f.confidence !== 'reported' && <SourceLink source={f.source} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setShowAll((s) => !s)}
          className="mt-2 text-xs text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent font-(family-name:--font-ui)"
        >
          {showAll ? `Hide the ${hidden} not-disclosed facts` : `+${hidden} not publicly disclosed — show all`}
        </button>
      )}
    </section>
  );
}

/* ---------- research activity (conference papers matched via affiliations) ---------- */

function ResearchCard({ slug }: { slug: string }) {
  const { data } = usePapers(0, '', slug, 0);
  if (!data || data.items.length === 0) return null; // most companies publish nothing — no empty card
  return (
    <Card>
      <span className="eyebrow">Research activity</span>
      <p className="mt-1 text-xs text-muted">
        SPE/IADC conference papers with an author affiliation matching this company.
      </p>
      <ol className="mt-2 flex flex-col">
        {data.items.slice(0, 5).map((p) => (
          <li key={p.id} className="flex gap-3 border-b border-border py-2 text-sm last:border-0">
            <span className="w-8 flex-shrink-0 text-right font-(family-name:--font-ui) font-bold text-accent [font-variant-numeric:tabular-nums]">
              {p.relevancyScore == null ? '—' : Math.round(p.relevancyScore)}
            </span>
            <span className="min-w-0 flex-1">
              <a href={p.url} target="_blank" rel="noreferrer" className="text-text hover:text-accent hover:underline">
                {p.title}
              </a>
              <span className="block text-xs text-muted">
                {p.conference ?? 'Conference n/a'} · {fmtDate(p.publishedDate)}
              </span>
            </span>
          </li>
        ))}
      </ol>
    </Card>
  );
}

/* ---------- conference presence (exhibitor sweep, B2) ---------- */

function ExhibitorCard({ slug }: { slug: string }) {
  const { data } = useExhibitors(slug);
  if (!data || data.length === 0) return null; // most companies exhibit nowhere we sweep — no empty card
  return (
    <Card>
      <span className="eyebrow">Conference presence</span>
      <p className="mt-1 text-xs text-muted">
        Exhibitor-directory listings matching this company (advisory — swept from public
        conference sites).
      </p>
      <ol className="mt-2 flex flex-col">
        {data.slice(0, 5).map((x) => (
          <li
            key={`${x.conferenceSlug}-${x.exhibitorName}`}
            className="flex flex-wrap items-baseline gap-x-2 border-b border-border py-2 text-sm last:border-0"
          >
            <span className="text-text">{x.conferenceName ?? x.conferenceSlug}</span>
            <span className="text-xs text-muted">
              {[
                x.conferenceStartsOn ? fmtDate(x.conferenceStartsOn) : null,
                x.hall || x.stand ? `hall ${x.hall ?? '?'} · stand ${x.stand ?? '?'}` : null,
                x.country,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </li>
        ))}
      </ol>
    </Card>
  );
}

/* ---------- events timeline ---------- */

/** Raw payload keys → readable labels: "processType" / "next_deadline" → "Process type" / "Next deadline". */
function humanizePayloadKey(key: string): string {
  const spaced = key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function EventsTimeline({ dossier }: { dossier: CompanyDossierDto }) {
  const events = [...dossier.events].sort((a, b) => b.eventDate.localeCompare(a.eventDate));
  return (
    <Card>
      <span className="eyebrow">Events</span>
      {events.length === 0 ? (
        <p className="mt-2 text-sm italic text-muted opacity-70">No events on record.</p>
      ) : (
        <ol className="mt-3 flex flex-col">
          {events.map((e) => (
            <li key={e.id} className="flex gap-4 border-b border-border py-3 last:border-0">
              <span className="w-24 flex-shrink-0 pt-0.5 text-xs text-muted [font-variant-numeric:tabular-nums]">
                {fmtDate(e.eventDate)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <EventKindBadge kind={e.kind} />
                  {e.jurisdiction && <Flag countryCode={e.jurisdiction} />}
                  {e.source ? (
                    <SourceLink source={e.source} />
                  ) : (
                    <span
                      className="text-[0.65rem] uppercase tracking-wider text-amber-500 font-(family-name:--font-ui)"
                      title="Computed signal — estimated confidence, no source document (ADR-0009 exception)"
                    >
                      computed signal
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-text">{e.title}</p>
                {e.payload && (
                  <p className="mt-0.5 text-xs text-muted">
                    {Object.entries(e.payload)
                      .filter(([, v]) => v === null || typeof v !== 'object') // deadlines[] etc. render elsewhere; "[object Object]" never
                      .map(([k, v]) => `${humanizePayloadKey(k)}: ${String(v)}`)
                      .join(' · ')}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

/* ---------- ownership history ---------- */

function OwnershipHistory({ segments }: { segments: OwnershipSegment[] }) {
  const sorted = [...segments].sort((a, b) => a.position - b.position);
  return (
    <Card>
      <span className="eyebrow">Ownership history</span>
      <ol className="mt-3 flex flex-col gap-1.5 text-sm">
        {sorted.map((s) => (
          <li key={s.position} className="flex flex-wrap items-baseline gap-x-2">
            <span className={s.toDate === null ? 'font-semibold text-text' : 'text-text'}>{s.name}</span>
            {s.owner && <span className="text-xs text-muted">— {s.owner}</span>}
            {(s.fromDate || s.toDate) && (
              <span className="text-xs text-muted [font-variant-numeric:tabular-nums]">
                {/* "~" marks estimated/unknown dates — never rendered as stated facts (WWD) */}
                {s.dateConfidence === 'stated' ? '' : '~'}
                {s.fromDate ?? '?'}–{s.toDate ?? 'today'}
              </span>
            )}
            {s.source && <SourceLink source={s.source} />}
          </li>
        ))}
      </ol>
    </Card>
  );
}
